import React, { useState } from 'react';
import SEO from '../components/SEO';

const CookiesPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('what-are-cookies');

  const sections = [
    { id: 'what-are-cookies', label: '1. What Are Cookies' },
    { id: 'how-we-use-cookies', label: '2. How We Use Cookies' },
    { id: 'types-of-cookies', label: '3. Types of Cookies We Use' },
    { id: 'third-party-cookies', label: '4. Third-Party Cookies' },
    { id: 'managing-cookies', label: '5. Managing Cookie Preferences' },
    { id: 'more-information', label: '6. More Information' }
  ];

  const handleSectionClick = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Offset for fixed header
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO 
        title="Cookies Policy - AlmeOne"
        description="Read AlmeOne's cookies policy to learn how and why we use cookies on our website and digital services."
        keywords="cookies policy, cookie preference, AlmeOne cookies, website cookies, privacy cookies"
      />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-gold/20 py-20 text-white">
        <div className="max-w-6xl mx-auto px-5 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gold">Cookies</span> Policy
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Last updated: June 17, 2026. This policy outlines how AlmeOne uses cookies to enhance your browsing experience.
          </p>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-1/4 lg:sticky lg:top-28 h-fit bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
            <h2 className="text-black font-bold text-lg mb-6 border-b border-gray-100 pb-3 flex items-center gap-2">
              <span className="text-gold text-xl">🍪</span> Table of Contents
            </h2>
            <nav className="space-y-1">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => handleSectionClick(section.id)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-gold/10 text-gold border-l-4 border-gold pl-3'
                      : 'text-gray-600 hover:text-gold hover:bg-gray-50 pl-4'
                  }`}
                >
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Cookies Content */}
          <div className="lg:w-3/4 bg-white p-8 sm:p-12 rounded-2xl shadow-lg border border-gray-100 prose prose-lg prose-gold max-w-none text-gray-700 leading-relaxed space-y-12">
            <div id="what-are-cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                1. What Are Cookies
              </h2>
              <p>
                Cookies are small text files placed on your computer or mobile device by websites that you visit. They are widely used to make websites work, or work more efficiently, as well as to provide statistical information to the owners of the site.
              </p>
              <p>
                A cookie can contain information such as a user ID that the site uses to track the pages you have visited. The only personal information a cookie can contain is information you supply yourself.
              </p>
            </div>

            <div id="how-we-use-cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                2. How We Use Cookies
              </h2>
              <p>
                We use cookies for several reasons, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>To enable certain functions of the website (such as contact forms and Recaptcha security checks).</li>
                <li>To provide analytics and understand how visitors interact with our content, enabling us to optimize the website architecture and design.</li>
                <li>To store your preferences and prevent you from having to re-enter information when navigating between pages.</li>
              </ul>
            </div>

            <div id="types-of-cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                3. Types of Cookies We Use
              </h2>
              <p>
                We use the following categories of cookies:
              </p>
              <div className="space-y-4 mt-4">
                <div className="border-l-4 border-gold bg-gray-50 p-4 rounded-r-lg">
                  <h4 className="font-bold text-black">Essential / Strictly Necessary Cookies:</h4>
                  <p className="text-sm text-gray-600">
                    These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually set in response to actions made by you, such as requesting services, filling in forms, or logging in.
                  </p>
                </div>
                <div className="border-l-4 border-gold bg-gray-50 p-4 rounded-r-lg">
                  <h4 className="font-bold text-black">Performance & Analytics Cookies:</h4>
                  <p className="text-sm text-gray-600">
                    These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us know which pages are the most and least popular and see how visitors move around the site.
                  </p>
                </div>
                <div className="border-l-4 border-gold bg-gray-50 p-4 rounded-r-lg">
                  <h4 className="font-bold text-black">Functional Cookies:</h4>
                  <p className="text-sm text-gray-600">
                    These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
                  </p>
                </div>
              </div>
            </div>

            <div id="third-party-cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                4. Third-Party Cookies
              </h2>
              <p>
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the website, deliver advertisements, and prevent abuse:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>
                  <strong className="text-black">Google Analytics:</strong> We use Google Analytics to help us understand how customers use the site. You can read more about how Google uses your Personal Information in Google's privacy policy.
                </li>
                <li>
                  <strong className="text-black">Google ReCaptcha:</strong> To prevent bots and spam from submitting forms on our contact page, we load Google ReCaptcha. It installs cookies to perform security checks on user interactions.
                </li>
              </ul>
            </div>

            <div id="managing-cookies" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                5. Managing Cookie Preferences
              </h2>
              <p>
                Most web browsers allow you to control cookies through their settings preferences. However, if you limit the ability of websites to set cookies, you may worsen your overall user experience, as it will no longer be personalized to you. It may also stop you from saving customized settings like login information.
              </p>
              <p>
                To manage browser cookies, please consult the help documentation of your browser provider (Google Chrome, Mozilla Firefox, Apple Safari, Microsoft Edge, etc.).
              </p>
            </div>

            <div id="more-information" className="scroll-mt-28">
              <h2 className="text-2xl font-bold text-black mb-4 flex items-center gap-3">
                <span className="w-2 h-6 bg-gold rounded-full inline-block"></span>
                6. More Information
              </h2>
              <p>
                Hopefully, this has clarified things for you. If you are looking for more information, you can contact us through one of our preferred contact methods:
              </p>
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 space-y-2 mt-4 max-w-md">
                <p><strong className="text-black">Email:</strong> <a href="mailto:info@almeone.com" className="text-gold hover:underline">info@almeone.com</a></p>
                <p><strong className="text-black">Phone:</strong> +974 33920094</p>
                <p><strong className="text-black">Address:</strong> Doha, Qatar</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiesPage;
